/**
 * Which model the form will use, said where the form is.
 *
 * The Image page had no model choice at all: a person who had dismissed the
 * first-image card read « Choisissez un modèle dans la Bibliothèque » next to
 * a Generate button that refused, and had to guess the rail icon (audit
 * 03/10). This lists what is ready on the person's computer -- the readiness
 * answer, the one source every other place reads -- and leaves everything
 * else to the Library, one click away.
 */
import { APIClient } from "../../api/client.js";
import { h, setChildren } from "../../components/dom.js";
import { store } from "../../state/store.js";
import type { ModelInfo } from "../../types/index.js";

export interface ModelPicker {
	element: HTMLElement;
	/** Ask again (the page came back to view, a download finished). */
	refresh(): Promise<void>;
}

const OTHER = "__library__";

function openLibrary(): void {
	document.querySelector<HTMLButtonElement>('.rail-item[data-workspace="library"]')?.click();
}

/**
 * The image models to offer: those ready on the computer, plus the one
 * already chosen (picked in the Library, perhaps not downloaded yet), so the
 * list never contradicts the selection. Pure, for the test.
 */
export function modelsToOffer(
	models: ModelInfo[],
	readyIds: ReadonlySet<string>,
	selected: ModelInfo | null
): ModelInfo[] {
	const offered = models.filter((model) => readyIds.has(model.id));
	if (selected && !offered.some((model) => model.id === selected.id)) offered.unshift(selected);
	return offered;
}

export function createModelPicker(): ModelPicker {
	const select = h("select", { class: "model-picker-select", "aria-label": "Modèle" });
	const element = h("div", { class: "form-group model-picker" });
	let offered: ModelInfo[] = [];

	function render(): void {
		const selected = store.get().selectedModel;
		const library = h(
			"button",
			{ class: "btn btn-small", type: "button", onclick: openLibrary },
			"Voir tous les modèles"
		);
		const label = h("label", { class: "field-label" }, "Modèle");
		if (offered.length === 0) {
			setChildren(
				element,
				label,
				h(
					"p",
					{ class: "help-text" },
					"Aucun modèle n'est encore sur votre ordinateur. ",
					h(
						"button",
						{ class: "btn btn-primary btn-small", type: "button", onclick: openLibrary },
						"Choisir un modèle"
					)
				)
			);
			return;
		}
		const options = offered.map((model) =>
			h("option", { value: model.id }, model.name ?? model.id)
		);
		if (!selected) {
			options.unshift(h("option", { value: "", disabled: true }, "— Choisissez un modèle —"));
		}
		options.push(h("option", { value: OTHER }, "Autre modèle… (Bibliothèque)"));
		setChildren(select, ...options);
		select.value = selected?.id ?? "";
		setChildren(element, label, h("div", { class: "model-picker-row" }, select, library));
	}

	select.addEventListener("change", () => {
		if (select.value === OTHER) {
			select.value = store.get().selectedModel?.id ?? "";
			openLibrary();
			return;
		}
		const model = offered.find((candidate) => candidate.id === select.value) ?? null;
		if (model) store.setState({ selectedModel: model });
	});

	async function refresh(): Promise<void> {
		try {
			const [models, answers] = await Promise.all([
				APIClient.getModels(),
				APIClient.getReadiness(),
			]);
			const ready = new Set(
				answers.filter((answer) => !answer.family && answer.ready).map((a) => a.model_id)
			);
			offered = modelsToOffer(models, ready, store.get().selectedModel);
			// One model ready and none chosen: there is nothing to choose.
			if (!store.get().selectedModel && offered.length === 1) {
				store.setState({ selectedModel: offered[0] });
			}
		} catch {
			// Unreachable server: keep what was shown; the Library still works.
		}
		render();
	}

	store.subscribe((s) => s.selectedModel?.id ?? null, render);
	// A computer coming online, or reporting a model (a download finished),
	// changes what is ready: the same key the first-image card listens to.
	store.subscribe(
		(s) =>
			(s.workers ?? [])
				.filter((worker) => worker.online !== false)
				.map(
					(worker) =>
						`${worker.worker_id}:${Object.keys(worker.cached_models_gb ?? {})
							.sort()
							.join(",")}:${Object.keys(worker.incomplete_models ?? {})
							.sort()
							.join(",")}`
				)
				.join("|"),
		() => void refresh()
	);
	render();
	return { element, refresh };
}
